import {
  TimeoutController,
  createSafeTimeout,
  AnyFunc,
  createEventEmitter,
  Error,
  EventEmitter,
  eventObject,
  Logger,
} from '@quickplay/foundation';

import {
  Action,
  AdComposedPlayer,
  AdEvent,
  AdPlayer,
  AdTechnology,
  PlaybackOptions,
  Player,
  PlayerContext,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  TrackInfo,
  TrackType,
  TrackVariantInfo,
} from '@quickplay/player-interface';

import { PlayerBuilder } from '../../PlayerBuilder';
import { createIMAAdPlayer } from '../IMAAdsPlayer';
import { SsaiAdComposedPlayer } from '../../models/SsaiAdComposedPlayer';
import { AFCPlaybackSession, createAFCPlaybackSessionManager } from '@quickplay/player';

/**
 * Creates an `SsaiAdPlayer` by composing content `Player` and internally created
 * Ad `Player`.
 *
 * The newly created composed `Player` manages the complexities of Ad loading,
 * Ad Management, switching the context between content and ad, etc.
 *
 * @param builder PlayerBuilder Ad PlayerBuilder instance.
 * @returns ImaSsaiAdsPlayer A new player instance that composes Ad and Content Players
 */

/* eslint-disable sonarjs/cognitive-complexity */
export function createSsaiComposableAdPlayer(builder: PlayerBuilder): SsaiAdComposedPlayer {
  const logger: Logger = builder.configuredLogger;
  const timeoutController: TimeoutController = createSafeTimeout();
  const fallbackTimeoutMs = 500;
  const eventEmitter: EventEmitter = createEventEmitter();
  const contentPlayer: Player = builder.player;
  const adPlayer: AdPlayer = createIMAAdPlayer(builder);
  const isFallback = builder.imaDaiStreamManager?.isFallback || false;
  let adFrequencySession: AFCPlaybackSession | undefined = undefined;

  let internalPlayerContext: PlayerContext = PlayerContext.MAIN;
  let previousVpmute: number | undefined;
  let currentAdTagParameters: Record<string, any> = builder.imaDaiStreamManager?.getAdTagParameters() || {};

  /**
   * Handle fallback scenario for ad initialization
   *
   * It uses a timeout to trigger the fallback
   * process if necessary.
   */
  if (isFallback) {
    // Set a safety timeout for fallback scenario
    timeoutController.set(() => {
      eventEmitter.publish('aderror', builder.imaDaiStreamManager?.reason);
      logger.log('[DAI] Fallback occured, Error event has emited with reason');
    }, fallbackTimeoutMs);
  } else {
    // If not in fallback, clear any existing timeout
    timeoutController.clear();
  }

  const isAdContext = (): boolean => {
    return internalPlayerContext === PlayerContext.AD;
  };

  const switchToContext = (context: PlayerContext): void => {
    internalPlayerContext = context;
    logger.info(`Switched to context: ${internalPlayerContext}`);
    eventEmitter.publish('playercontextchanged', internalPlayerContext);
  };

  const instanceOfAdComposedPlayer = (player: Player): player is AdComposedPlayer => {
    return 'playerContext' in player;
  };

  const updateLiveAdTagParameters = (): void => {
    if (contentPlayer.isLive && !isFallback) {
      builder.imaDaiStreamManager.streamManager.replaceAdTagParameters(currentAdTagParameters);
    }
  };

  const onAdBreakStart = (): void => switchToContext(PlayerContext.AD);
  const onAdBreakEnd = (): void => switchToContext(PlayerContext.MAIN);
  const onContentVolumeChanged = (): void => {
    const vpmute = contentPlayer.isMute || contentPlayer.volume === 0 ? 1 : 0;

    if (vpmute === previousVpmute) {
      return;
    }

    previousVpmute = vpmute;

    currentAdTagParameters = {
      ...currentAdTagParameters,
      vpmute,
    };
    updateLiveAdTagParameters();
  };

  const subscribeInternalEvents = (): void => {
    adPlayer.subscribe(AdEvent.ADVERT_BREAK_START, onAdBreakStart);
    adPlayer.subscribe(AdEvent.ADVERT_BREAK_END, onAdBreakEnd);

    contentPlayer.subscribe('volumechanged', onContentVolumeChanged);
  };

  const unsubscribeInternalEvents = (): void => {
    adPlayer.unsubscribe(AdEvent.ADVERT_BREAK_START, onAdBreakStart);
    adPlayer.unsubscribe(AdEvent.ADVERT_BREAK_END, onAdBreakEnd);

    contentPlayer.unsubscribe('volumechanged', onContentVolumeChanged);
  };

  const composableAdPlayer: SsaiAdComposedPlayer = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.subscribe(eventName, callback);
      contentPlayer.subscribe(eventName, callback);
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.unsubscribe(eventName, callback);
      contentPlayer.unsubscribe(eventName, callback);
      eventEmitter.unsubscribe(eventName, callback);
    },

    get adSessionId(): string | undefined {
      return builder.imaDaiStreamManager?.streamId();
    },

    get playerContext(): PlayerContext {
      if (!isAdContext() && instanceOfAdComposedPlayer(contentPlayer)) {
        return contentPlayer.playerContext;
      }

      return internalPlayerContext;
    },

    get playerState() {
      return isAdContext() ? adPlayer.playerState : contentPlayer.playerState;
    },

    get playbackRate() {
      return contentPlayer.playbackRate;
    },

    set playbackRate(rate) {
      if (!isAdContext()) {
        contentPlayer.playbackRate = rate;
      }
    },

    get availableBitrates() {
      return isAdContext() ? adPlayer.availableBitrates : contentPlayer.availableBitrates;
    },

    set minBitrate(minBitrate) {
      if (!isAdContext()) {
        contentPlayer.minBitrate = minBitrate;
      }
    },

    get minBitrate() {
      return isAdContext() ? adPlayer.minBitrate : contentPlayer.minBitrate;
    },

    set maxBitrate(maxBitrate) {
      if (!isAdContext()) {
        contentPlayer.maxBitrate = maxBitrate;
      }
    },

    get maxBitrate() {
      return isAdContext() ? adPlayer.maxBitrate : contentPlayer.maxBitrate;
    },

    get internalPlayerName() {
      return isAdContext() ? adPlayer.internalPlayerName : contentPlayer.internalPlayerName;
    },

    get internalPlayerVersion() {
      return isAdContext() ? adPlayer.internalPlayerVersion : contentPlayer.internalPlayerVersion;
    },

    get rawPlayer() {
      return contentPlayer.rawPlayer;
    },

    get rawLibrary() {
      return contentPlayer.rawLibrary;
    },

    get isBuffering() {
      return isAdContext() ? adPlayer.isBuffering : contentPlayer.isBuffering;
    },

    get isSeeking() {
      return isAdContext() ? adPlayer.isSeeking : contentPlayer.isSeeking;
    },

    get currentTime() {
      return adPlayer.currentTime;
    },

    get currentEpochTime() {
      return isAdContext() ? adPlayer.currentEpochTime : contentPlayer.currentEpochTime;
    },

    get duration() {
      return adPlayer.duration;
    },

    get playbackStatistics() {
      return contentPlayer.playbackStatistics;
    },

    get isLive() {
      return contentPlayer.isLive;
    },

    get remainingTime() {
      // TODO: implement
      return isAdContext() ? adPlayer.remainingTime : -1;
    },

    get isMute() {
      return contentPlayer.isMute;
    },

    get playbackOptions() {
      return contentPlayer.playbackOptions;
    },

    get volume() {
      return contentPlayer.volume;
    },

    set volume(volumeLevel: number) {
      adPlayer.volume = volumeLevel;
      contentPlayer.volume = volumeLevel;
    },

    get adInsertionType(): AdTechnology {
      return AdTechnology.SSAI;
    },

    load(): void {
      adPlayer.load();
    },

    setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
      contentPlayer.setRequestInterceptor(requestInterceptor);
    },

    setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
      contentPlayer.setResponseInterceptor(responseInterceptor);
    },

    accessFunctionHandler(json: string): void {
      contentPlayer.accessFunctionHandler(json);
    },

    /**
     * Replaces Ad Tag Parameters specifically for Live streams.
     *
     * This function utilizes the StreamManager's replaceAdTagParameters method
     * to update ad tag parameters dynamically for live content.
     *
     * @see {@link https://developers.google.com/ad-manager/dynamic-ad-insertion/sdk/html5/reference/js/StreamManager#replaceAdTagParameters|Google Ad Manager DAI SDK - replaceAdTagParameters}
     *
     * @param {Object} params - The new ad tag parameters to replace the existing ones.
     * @returns {void}
     */
    setAdTagParameters(params: any): void {
      currentAdTagParameters = { ...params };
      updateLiveAdTagParameters();
      logger.log('[DAI][MANAGER] Replace AdTagParameters', currentAdTagParameters);
    },

    stop(): Promise<void> {
      adFrequencySession?.dispose();
      unsubscribeInternalEvents();
      if (isAdContext()) {
        return adPlayer.stop().finally(() => contentPlayer.stop());
      }
      return contentPlayer.stop();
    },

    play(): void {
      adPlayer.play();
    },

    pause(): void {
      contentPlayer.pause();
    },

    abort: (error: Error) => {
      unsubscribeInternalEvents();
      contentPlayer.abort(error);
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      adPlayer.send(eventName, action, metadata);
      contentPlayer.send(eventName, action, metadata);
    },

    seek(seekPoint: number): void {
      adPlayer.seek(seekPoint);
    },

    async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return isAdContext() ? adPlayer.getThumbnail(position) : contentPlayer.getThumbnail(position);
    },

    seekToLiveEdge(): void {
      isAdContext() ? adPlayer.seekToLiveEdge() : contentPlayer.seekToLiveEdge();
    },

    seekableRange(): SeekRangeInfo {
      if (contentPlayer.isLive) {
        return contentPlayer.seekableRange();
      }

      return adPlayer.seekableRange();
    },

    currentOffsetFromLiveEdge(): number {
      return isAdContext() ? adPlayer.currentOffsetFromLiveEdge() : contentPlayer.currentOffsetFromLiveEdge();
    },

    getAllTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getAllTracks();
    },

    getActiveTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getActiveTracks();
    },

    getVariantTracks(): TrackVariantInfo[] {
      return contentPlayer.getVariantTracks();
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      return contentPlayer.getAvailableTrackVariants(trackType);
    },

    selectTrack(track?: TrackInfo): void {
      !isAdContext() && contentPlayer.selectTrack(track);
    },

    muteOrUnmute(): void {
      adPlayer.muteOrUnmute && adPlayer.muteOrUnmute();
      contentPlayer.muteOrUnmute && contentPlayer.muteOrUnmute();
    },

    skipAd(): void {
      if (isAdContext()) {
        adPlayer.skipAd();
      } else {
        logger.warn(`Unsupported operation 'skipAd' in ${internalPlayerContext} context`);
      }
    },

    resize(width: number, height: number, isFullscreen: boolean): void {
      adPlayer.resize(width, height, isFullscreen);
    },

    discardAdBreak(): void {
      if (isAdContext()) {
        adPlayer.discardAdBreak();
      } else {
        logger.warn(`Unsupported operation 'discardAdBreak' in ${internalPlayerContext} context`);
      }
    },

    getCuePoints(): number[] {
      return adPlayer.getCuePoints();
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      contentPlayer.setPlaybackOptions(playbackOptions);
    },

    restrictQuality(enableRestriction: boolean): void {
      contentPlayer.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return contentPlayer.availableMimeTypes(trackType);
    },

    unloadAndSavePreload(): Promise<void> {
      // no-op
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // no-op
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return contentPlayer.setPreferredMimeTypes(trackType, mimeTypes);
    },
  };

  adFrequencySession = builder.adFrequencyControlConfiguration
    ? createAFCPlaybackSessionManager({
        player: composableAdPlayer,
        adFrequencyControlConfiguration: builder.adFrequencyControlConfiguration,
        logger: logger,
      })
    : undefined;

  subscribeInternalEvents();

  return composableAdPlayer;
}
